import { GameType } from './LobbyManager'
import { lobby } from '@vgw/pok-poker-lobby-client-lib'
import { ClientConfig } from './config/ClientConfig'
import { HttpService } from './services/http-service'
import { AuthService } from './auth/AuthService'
import { Utils } from './Utils'
import LobbyType = lobby.protocol.LobbyType
import { FeatureToggleService } from './services/FeatureToggleService'
import { LogManager } from './LogManager'

interface PokerLobbyTokenResponse {
	token: string
}

const MAX_RECONNECTION_ATTEMPTS = 8

export default new (class {
	private lobbySessionService: lobby.service.PokerLobbySessionService
  	private completeInitialization: () => void
  	private initialized: Promise<void> = new Promise<void>(function(resolve: () => void) {
	  this.completeInitialization = resolve
	}.bind(this))
	private enabledLobbyTypes: LobbyType[] = []
	private playerId: string | undefined
	private subscribedGames = new Set<string>()
	private subscribedGameTypes = new Set<GameType>()
	private subscribedToPlayerGames = false
	private subscribedToNotifications = false
	private retryAttempts = 0
	private playerGamesEventHandler: (event: lobby.protocol.LobbyPlayerGameEvent) => void = () => { throw Error('No event handler for player games')}
	private lobbyListEventHandler: (event: lobby.protocol.LobbyListEvent) => void = () => { throw Error('No event handler for player games')}
	private lobbyInstanceEventHandler: (event: lobby.protocol.LobbyInstanceEvent) => void = () => { throw Error('No event handler for lobby instance')}
	private notificationEventHandler: (event: lobby.protocol.NotificationEvent) => void = () => { throw Error('No event handler for notifications')}


	async initialize(token: string = undefined) {
		if (ClientConfig.POKER_LOBBY_GATEWAY.sngEnabled) {
			this.enabledLobbyTypes.push(LobbyType.SitAndGo)
		}
		if (FeatureToggleService.RingGamesFeature.includeNewPokerServerRingGames) {
			this.enabledLobbyTypes.push(LobbyType.RingGame)
		}
		if (FeatureToggleService.ScheduledTournamentsFeature.includeNewPokerServerTournaments) {
			this.enabledLobbyTypes.push(LobbyType.Scheduled)
		}
		const pokerLobbyAuthToken = token !== undefined ? token : await this.authenticateWithPokerLobbyService()
		const userId = await AuthService.getCurrentUserId()
		this.lobbySessionService = lobby.service.PokerLobbySessionService.build(
			`${ClientConfig.POKER_LOBBY_WEB_SOCKET_URL}/player/v1/poker-lobby/${userId}/ws?token=${pokerLobbyAuthToken}`,
			Number(userId),
		)
		this.lobbySessionService.registerPlayerSubscribedToLobbyList((ev) => {
			this.lobbyListEventHandler(ev)
		})
		this.lobbySessionService.registerLobbyListGameAdded((ev) => {
			this.lobbyListEventHandler(ev)
		})
		this.lobbySessionService.registerLobbyListGameUpdated((ev) => {
			this.lobbyListEventHandler(ev)
		})
		this.lobbySessionService.registerLobbyListGameRemoved((ev) => {
			this.lobbyListEventHandler(ev)
		})
		this.lobbySessionService.registerPlayerSubscribedToPlayerGames((ev) => {
			this.playerGamesEventHandler(ev)
		})
		this.lobbySessionService.registerPlayerGameAdded((ev) => {
			this.playerGamesEventHandler(ev)
		})
		this.lobbySessionService.registerPlayerGameUpdated((ev) => {
			this.playerGamesEventHandler(ev)
		})
		this.lobbySessionService.registerPlayerGameRemoved((ev) => {
			this.playerGamesEventHandler(ev)
		})
		this.lobbySessionService.registerPlayerGameWalletBalanceUpdated((ev) => {
			this.playerGamesEventHandler(ev)
		})
		this.lobbySessionService.registerPlayerSubscribedToLobbyInstance((ev) => {
			this.lobbyInstanceEventHandler(ev)
		})
		this.lobbySessionService.registerLobbyInstanceGameUpdated((ev) => {
			this.lobbyInstanceEventHandler(ev)
		})
		this.lobbySessionService.registerNotificationSubscribed((ev) => {
			this.notificationEventHandler(ev)
		})
		this.lobbySessionService.registerNotificationAdded((ev) => {
			this.notificationEventHandler(ev)
		})
		this.lobbySessionService.registerNotificationRemoved((ev) => {
			this.notificationEventHandler(ev)
		})
		this.lobbySessionService.registerCloseListener((code) => {
			switch (code) {
				case 1006: // Server deployment
					this.attemptToReconnectToLobbyService(pokerLobbyAuthToken)
					break
				case 1012:	// Service restart
					this.attemptToReconnectToLobbyService(pokerLobbyAuthToken)
					break
				case 1008:	// Invalid token
					this.attemptToReconnectToLobbyService()
					break
			}
		})
		this.lobbySessionService.openSession(() => {
			this.retryAttempts = 0
			this.reopenExistingSubscriptions()
			this.completeInitialization()
		}, (message: string) => {
			LogManager.error({ message })
		})
	}

	private async attemptToReconnectToLobbyService(token: string | undefined = undefined) {
		if (this.retryAttempts >= MAX_RECONNECTION_ATTEMPTS) throw Error('Maximum attempts to reconnect to lobby server reached')
		try {
			const delay = Math.max(1000 * (this.retryAttempts * 2), 1000)
			await Utils.sleep(delay)
			this.retryAttempts++
			await this.initialize(token)
		} catch (ex) {
			// if we have an invalid token, it will attempt to retrieve a new token from the server
			// catch any errors here and retry
			await this.attemptToReconnectToLobbyService()
		}
	}

	private reopenExistingSubscriptions() {
		this.subscribedGameTypes.forEach((gameType) => {
			this.subscribeToLobbyList(gameType)
		})
		if (this.subscribedToPlayerGames) {
			this.lobbySessionService.subscribeToPlayerGames()
		}
		this.subscribedGames.forEach((gameId) => {
			this.subscribeToLobbyInstance(gameId)
		})
		if (this.subscribedToNotifications) {
			this.lobbySessionService.subscribeToNotifications()
		}
	}

	private async authenticateWithPokerLobbyService(): Promise<string> {
		const playerId = await this.getPlayerId()
		const url = `${ClientConfig.POKER_LOBBY_API_URL}/player/v1/auth/${playerId}`
		const response = await HttpService.post<PokerLobbyTokenResponse>(url, {}, { authenticate: true })
		return response.token
	}
	private async getPlayerId(): Promise<string> {
		if (!this.playerId) this.playerId = await AuthService.getCurrentUserId()
		return this.playerId
	}

	registerLobbyListEventHandler(eventHandler: (event: lobby.protocol.LobbyListEvent) => void) {
		this.lobbyListEventHandler = eventHandler
	}

	registerLobbyInstanceEventHandler(eventHandler: (event: lobby.protocol.LobbyInstanceEvent) => void) {
		this.lobbyInstanceEventHandler = eventHandler
	}

	async subscribeToPlayerGames(eventHandler: (event: lobby.protocol.LobbyPlayerGameEvent) => void) {
		await this.initialized
		this.playerGamesEventHandler = eventHandler
		this.lobbySessionService.subscribeToPlayerGames()
		this.subscribedToPlayerGames = true
	}

	async subscribeToLobbyList(gameType: GameType) {
		await this.initialized
		let lobbyType = mapToLobbyType(gameType)
		if (this.enabledLobbyTypes.indexOf(lobbyType) > -1) {
			this.subscribedGameTypes.add(gameType)
			this.lobbySessionService.subscribeToLobbyList(lobbyType)
		}
	}

	async subscribeToLobbyInstance(gameId: string) {
		await this.initialized
		this.subscribedGames.add(gameId)
		this.lobbySessionService.subscribeToLobbyInstance(gameId)
	}

	async unsubscribeToLobbyInstance(gameId: string) {
		await this.initialized
		this.subscribedGames.delete(gameId)
		this.lobbySessionService.unsubscribeFromLobbyInstance(gameId)
	}

	registerNotificationEventHandler(eventHandler: (event: lobby.protocol.NotificationEvent) => void) {
		this.notificationEventHandler = eventHandler
	}

	async subscribeToNotifications() {
		await this.initialized
		this.lobbySessionService.subscribeToNotifications()
		this.subscribedToNotifications = true
	}

	async unsubscribeFromNotifications() {
		await this.initialized
		this.lobbySessionService.unsubscribeFromNotifications()
		this.subscribedToNotifications = false
	}

	async unsubscribeFromLobbyList(gameType: GameType) {
		await this.initialized
		let lobbyType = mapToLobbyType(gameType)
		if (this.enabledLobbyTypes.indexOf(lobbyType) > -1) {
			this.subscribedGameTypes.delete(gameType)
			this.lobbySessionService.unsubscribeFromLobbyList(lobbyType)
		}
	}
})()

const mapToLobbyType = (from: GameType): LobbyType => {
	switch (from) {
		case GameType.SitAndGo:
			return LobbyType.SitAndGo
		case GameType.Ring:
			return LobbyType.RingGame
		case GameType.Tournament:
			return LobbyType.Scheduled
	}
}
